import { ReactNode, useCallback } from 'react'
import { isNil } from 'lodash'
import {
  faRibbon,
  faUpRightFromSquare,
  faSquareArrowUpRight,
} from '@fortawesome/pro-regular-svg-icons'
import { useFlyoutV2, useReselector } from '@/utils/sharedHooks'
import { selectUserTaxEstimateById } from '../userTaxEstimates.selector'
import {
  Accordion,
  Badge,
  Button,
  Card,
  CardColors,
  Icon,
  Link,
  Image,
  Text,
} from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { splitTaxYearQuarter } from '../helpers'
import { shortNames } from '@/constants/statesShortNamesConstants'
import { SafeHarborType } from '../userTaxEstimates.slice'
import { TextColors, TypographyStyles } from '@/styles'
import { selectQuarterlyTaxEstimateDetailsByYearAndQuarter } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { STATE_QUARTERLY_TAX_PAYMENTS } from '@/features/taxes/taxConstants'

import { ParsedHtml } from '@/componentsV1/BaseComponents'

const StepCard = ({
  stepNumber,
  content,
  expandContent,
}: {
  stepNumber: number
  content: ReactNode
  expandContent?: ReactNode
}) => (
  <div className="mb-6">
    <div className="flex items-start gap-4">
      <Badge color="gray">{stepNumber}</Badge>
      <div className="flex-1">{content}</div>
    </div>
    {expandContent && (
      <div className="ml-12 mt-3">
        <Accordion
          type="single"
          items={[{ title: 'Show me', content: expandContent }]}
          variant="text"
          collapsible
        />
      </div>
    )}
  </div>
)

const TaxPaymentInfoFlyout = ({
  estimateId,
  stateHelpContent,
  amountInCentsOverride,
  headerOverride,
}: {
  estimateId?: number
  stateHelpContent?: string | null
  amountInCentsOverride?: number
  headerOverride?: string
}) => {
  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  const { quarter, year } = splitTaxYearQuarter(estimate?.taxQuarter)
  const estimateDetails = useReselector(
    selectQuarterlyTaxEstimateDetailsByYearAndQuarter,
    year,
    quarter
  )

  if (!estimate) return null

  const { filingState, estimateInCents } = estimate

  const amountDue = () => {
    if (!isNil(amountInCentsOverride)) {
      return formatCurrencyFromCents(amountInCentsOverride)
    }
    if (!isNil(estimateInCents)) {
      return formatCurrencyFromCents(estimateInCents)
    }
    return '$ ---'
  }
  const longStateName = filingState && shortNames[filingState]
  const helpArticle = filingState
    ? STATE_QUARTERLY_TAX_PAYMENTS[filingState]?.helpArticle
    : null

  const estimateHeaderText =
    headerOverride ??
    (estimate?.safeHarborType === SafeHarborType.ES
      ? `Q${quarter} 1040-ES Estimate`
      : estimate?.safeHarborType
        ? `Q${quarter} Federal Safe Harbor Estimate`
        : `Q${quarter} ${longStateName || 'Federal'} Tax Estimate`)

  return (
    <div className="flex flex-col">
      <Card color={CardColors.EMPHASIS}>
        <Text variant={TypographyStyles.H3}>{estimateHeaderText}</Text>
        <Text variant={TypographyStyles.DISPLAY_LG}>{amountDue()}</Text>
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Estimated Payment due{' '}
          {estimateDetails?.irsPaymentDueAt &&
            isoToUTCDateTime(estimateDetails.irsPaymentDueAt).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_LONG
            )}
        </Text>
      </Card>

      <Card color={CardColors.LEVEL_1}>
        <Text
          variant={TypographyStyles.EYEBROW}
          color={TextColors.MUTED}
          className="inline-flex items-center"
        >
          <Icon icon={faRibbon} className="h-4 w-4 mr-2 display-inline-block" />
          Reminder
        </Text>
        <Text variant={TypographyStyles.H3} className="mt-2">
          Quarterly taxes are <i>personal</i> income taxes. Pay from your{' '}
          <i>personal</i> account.
        </Text>
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/4416233114647-Should-I-pay-quarterly-taxes-from-my-personal-or-business-bank-account"
          newPage
          className="mt-2 block"
        >
          Why?
        </Link>
      </Card>

      <Text variant={TypographyStyles.H3}>How to pay</Text>
      <br />
      {filingState ? (
        <>
          <StepCard
            stepNumber={1}
            content={
              <Text>
                You’ll be directed to the state portal to submit your tax
                payment. You can pay using your bank account or credit card.
                (Note: paying by credit card incurs fees).
              </Text>
            }
          />
          <StepCard
            stepNumber={2}
            content={
              <Text>
                Save your receipts and upload to Heard using “Mark as Paid.” We
                need this to keep an accurate record of your taxes.
              </Text>
            }
            expandContent={
              <Image
                alt="Federal Payments"
                src="https://heard-images.s3.amazonaws.com/assets/FederalPayments3.png"
              />
            }
          />
          {stateHelpContent && (
            <Accordion variant="text" type="single" collapsible>
              <Accordion.Item value="detailed-instructions">
                <Accordion.Trigger>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    View detailed instructions
                  </Text>
                </Accordion.Trigger>
                <Accordion.Content>
                  <ParsedHtml>{stateHelpContent}</ParsedHtml>
                  {helpArticle && (
                    <div className="mt-4">
                      <Link href={helpArticle} newPage>
                        <Icon icon={faUpRightFromSquare} className="mr-2" />
                        View help article
                      </Link>
                    </div>
                  )}
                </Accordion.Content>
              </Accordion.Item>
            </Accordion>
          )}
        </>
      ) : (
        <>
          <StepCard
            stepNumber={1}
            content={
              <Text>
                Pay online through the IRS portal using your bank account or
                credit card. If paying by personal bank account, click “Direct
                Pay.”{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/4402339575319-Paying-Federal-Quarterly-Taxes"
                  newPage
                >
                  View help for other payment methods
                </Link>
              </Text>
            }
            expandContent={
              <>
                <Text>
                  You will be redirected to a secure portal and prompted to
                  answer several questions to confirm your tax information and
                  identity. On the first page of the portal, be sure to note
                  your Reason for Payment as “Estimated Tax”.
                </Text>
                <Image
                  alt="Federal Payments"
                  src="https://heard-images.s3.amazonaws.com/assets/FederalPayments1.png"
                />
              </>
            }
          />
          <StepCard
            stepNumber={2}
            content={
              <Text>
                If paying by Direct Pay (from your personal bank account), for
                “Apply Payment To” select Form 1040-Income Tax. For “Reason for
                Payment,” select Estimated Tax and enter the current year.
              </Text>
            }
            expandContent={
              <Image
                alt="Federal Payments"
                src="https://heard-images.s3.amazonaws.com/assets/FederalPayments2.png"
              />
            }
          />
          <StepCard
            stepNumber={3}
            content={
              <Text>
                Save your receipts and upload to Heard using “Mark as Paid.” We
                need this to keep an accurate record of your taxes.
              </Text>
            }
            expandContent={
              <Image
                alt="Federal Payments"
                src="https://heard-images.s3.amazonaws.com/assets/FederalPayments3.png"
              />
            }
          />
        </>
      )}
    </div>
  )
}

export const TaxPaymentInfoFlyoutFooter = ({
  estimateId,
  openMarkAsPaid,
}: {
  estimateId?: number
  openMarkAsPaid: () => void
}) => {
  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  if (!estimate) return null

  const paymentLink = estimate?.filingState
    ? STATE_QUARTERLY_TAX_PAYMENTS[estimate.filingState]?.paymentLink
    : 'https://www.irs.gov/payments'

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4">
      <div className="w-1/2">
        <Link newPage href={paymentLink}>
          <Button size="md" iconLeft={faSquareArrowUpRight} className="w-full">
            Continue to payment
          </Button>
        </Link>
      </div>
      <div className="w-1/2">
        <Button
          variant="secondary"
          className="w-full"
          onClick={openMarkAsPaid}
          size="md"
        >
          Mark as paid
        </Button>
      </div>
    </div>
  )
}

const useTaxPaymentInfoFlyout = ({
  estimateId,
  openQTEModal,
  amountInCentsOverride,
  headerOverride,
}: {
  estimateId?: number
  openQTEModal: () => void
  /** Safe harbor callers pass the catch-up total and a label naming it. */
  amountInCentsOverride?: number
  headerOverride?: string
}) => {
  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  const { openFlyout, closeFlyout } = useFlyoutV2()

  const helpArticleId = estimate?.filingState
    ? STATE_QUARTERLY_TAX_PAYMENTS[estimate?.filingState]?.helpArticleId
    : null

  const fetchArticleContent = useCallback(async () => {
    if (!helpArticleId) return null

    try {
      const response = await fetch(
        `https://joinheard.zendesk.com/api/v2/help_center/en-us/articles/${helpArticleId}.json`
      )
      const json = await response.json()
      return typeof json?.article?.body === 'string' ? json.article.body : null
    } catch {
      return null
    }
  }, [helpArticleId])

  return useCallback(async () => {
    const content = await fetchArticleContent()
    const longStateName =
      estimate?.filingState && shortNames[estimate?.filingState]

    openFlyout({
      title: `Pay ${estimate?.filingState ? longStateName : 'federal'} quarterly taxes`,
      direction: 'right',
      content: (
        <TaxPaymentInfoFlyout
          estimateId={estimateId}
          stateHelpContent={content}
          amountInCentsOverride={amountInCentsOverride}
          headerOverride={headerOverride}
        />
      ),
      footer: (
        <TaxPaymentInfoFlyoutFooter
          estimateId={estimateId}
          openMarkAsPaid={() => {
            closeFlyout()
            openQTEModal()
          }}
        />
      ),
    })
  }, [
    fetchArticleContent,
    estimate?.filingState,
    amountInCentsOverride,
    headerOverride,
    openFlyout,
    estimateId,
    closeFlyout,
    openQTEModal,
  ])
}

export default useTaxPaymentInfoFlyout
